import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentContextType {
  showBanner: boolean;
  preferences: CookiePreferences;
  hasConsented: boolean;
  updatePreferences: (newPreferences: CookiePreferences) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: () => void;
  resetConsent: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const STORAGE_KEY = 'wabisabi_cookie_consent';
const CONSENT_VERSION = '1.0'; // Increment when cookie policy changes

interface StoredConsent {
  preferences: CookiePreferences;
  timestamp: number;
  version: string;
}

const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

interface CookieConsentProviderProps {
  children: ReactNode;
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({ children }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);
  const [hasConsented, setHasConsented] = useState(false);

  useEffect(() => {
    const checkStoredConsent = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const consent: StoredConsent = JSON.parse(stored);
          
          // Check if consent is still valid (same version and not older than 1 year)
          const oneYearAgo = Date.now() - (365 * 24 * 60 * 60 * 1000);
          const isValid = consent.version === CONSENT_VERSION && consent.timestamp > oneYearAgo;
          
          if (isValid) {
            setPreferences(consent.preferences);
            setHasConsented(true);
            setShowBanner(false);
            
            // Apply preferences immediately
            applyConsentPreferences(consent.preferences);
            return;
          } else {
            // Remove outdated consent
            localStorage.removeItem(STORAGE_KEY);
          }
        }
        
        // Show banner if no valid consent found
        setShowBanner(true);
      } catch (error) {
        console.error('Error reading cookie consent:', error);
        setShowBanner(true);
      }
    };

    // Delay showing banner to avoid flash on initial load
    const timer = setTimeout(checkStoredConsent, 1000);
    return () => clearTimeout(timer);
  }, []);

  const applyConsentPreferences = (prefs: CookiePreferences) => {
    // Update Google Analytics consent
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: prefs.analytics ? 'granted' : 'denied',
        ad_storage: prefs.marketing ? 'granted' : 'denied',
        ad_user_data: prefs.marketing ? 'granted' : 'denied',
        ad_personalization: prefs.marketing ? 'granted' : 'denied',
      });
    }

    // Clear analytics cookies if consent withdrawn
    if (!prefs.analytics) {
      clearAnalyticsCookies();
    }

    // Clear marketing cookies if consent withdrawn
    if (!prefs.marketing) {
      clearMarketingCookies();
    }
  };

  const clearAnalyticsCookies = () => {
    // Clear Google Analytics cookies
    const cookiesToClear = [
      '_ga',
      '_ga_*',
      '_gid',
      '_gat',
      '_gat_*',
      '__utma',
      '__utmb',
      '__utmc',
      '__utmt',
      '__utmz',
    ];

    cookiesToClear.forEach(cookieName => {
      if (cookieName.includes('*')) {
        // Handle wildcard cookies
        const prefix = cookieName.replace('*', '');
        Object.keys(document.cookie.split(';')).forEach(cookie => {
          const name = cookie.split('=')[0].trim();
          if (name.startsWith(prefix)) {
            deleteCookie(name);
          }
        });
      } else {
        deleteCookie(cookieName);
      }
    });
  };

  const clearMarketingCookies = () => {
    // Clear common marketing/advertising cookies
    const marketingCookies = [
      '_fbp',
      '_fbc',
      'fr',
      'tr',
      '_gcl_au',
      '_gcl_aw',
      '_gcl_dc',
      '__Secure-FPLC',
      '__Secure-FPGC',
    ];

    marketingCookies.forEach(deleteCookie);
  };

  const deleteCookie = (name: string) => {
    const domains = [window.location.hostname, `.${window.location.hostname}`];
    const paths = ['/', '/dashboard', '/auth'];

    domains.forEach(domain => {
      paths.forEach(path => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${domain}; path=${path}`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
      });
    });
  };

  const saveConsent = (prefs: CookiePreferences) => {
    const consent: StoredConsent = {
      preferences: prefs,
      timestamp: Date.now(),
      version: CONSENT_VERSION,
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
      setPreferences(prefs);
      setHasConsented(true);
      setShowBanner(false);
      applyConsentPreferences(prefs);
    } catch (error) {
      console.error('Error saving cookie consent:', error);
    }
  };

  const updatePreferences = (newPreferences: CookiePreferences) => {
    setPreferences(newPreferences);
  };

  const acceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    saveConsent(allAccepted);
  };

  const rejectAll = () => {
    const onlyNecessary: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    saveConsent(onlyNecessary);
  };

  const savePreferences = () => {
    saveConsent(preferences);
  };

  const resetConsent = () => {
    localStorage.removeItem(STORAGE_KEY);
    setPreferences(defaultPreferences);
    setHasConsented(false);
    setShowBanner(true);
  };

  const value: CookieConsentContextType = {
    showBanner,
    preferences,
    hasConsented,
    updatePreferences,
    acceptAll,
    rejectAll,
    savePreferences,
    resetConsent,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = (): CookieConsentContextType => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

export default CookieConsentProvider;